import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Currency,
  FrameworkAgreementDto,
  useCreateDirectCallOffMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { displaySum } from "../../common/formatting/displaySum";
import { NumericInput } from "../../common/input/NumericInput";
import { urls } from "../../urls";
import {
  activePanel,
  byRank,
  defaultCallOffSupplier,
  remainingUnderCeiling,
  sharingBestRank,
} from "./callOffAvailability";

export const NewCallOffModal = ({
  framework,
  supplierOrganizationId: chosenSupplier,
  isOpen,
  onClose,
}: {
  framework: FrameworkAgreementDto;
  supplierOrganizationId?: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const [create, { isLoading }] = useCreateDirectCallOffMutation();
  const activeMembers = byRank(activePanel(framework));
  const tiedAtTheTop = sharingBestRank(activeMembers);
  const [supplierOrganizationId, setSupplier] = useState(chosenSupplier ?? defaultCallOffSupplier(activeMembers));
  const [title, setTitle] = useState("");
  const [estimatedValue, setEstimatedValue] = useState<number | null>(null);
  const currency = framework.ceilingValue?.currency ?? Currency.Nok;
  const remaining = remainingUnderCeiling(framework);
  const remainingText = remaining === null ? "" : displaySum({ value: remaining, currency });
  const overCeiling = remaining !== null && estimatedValue !== null && estimatedValue > remaining;
  const valueMissing = remaining !== null && estimatedValue === null;

  const submit = async () => {
    try {
      const contract = await create({
        frameworkAgreementId: framework.id,
        createDirectCallOffRequest: {
          supplierOrganizationId,
          title: title.trim(),
          estimatedValue: estimatedValue === null ? undefined : { value: estimatedValue, currency },
        },
      }).unwrap();
      onClose();
      navigate(urls.app.contracts.view.go(contract.id));
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="smModalBackground">
        <ModalHeader>{t("New call-off")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <FormControl>
              <FormLabel htmlFor="call-off-supplier">{t("Supplier")}</FormLabel>
              <Select
                id="call-off-supplier"
                backgroundColor="smBackground"
                value={supplierOrganizationId}
                isDisabled={chosenSupplier !== undefined}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder={supplierOrganizationId === "" ? (t("Choose the supplier") ?? undefined) : undefined}
              >
                {activeMembers.map((member) => (
                  <option key={member.organizationId} value={member.organizationId}>
                    {member.ranking ? `${member.ranking}. ${member.name}` : member.name}
                  </option>
                ))}
              </Select>
              {chosenSupplier === undefined && tiedAtTheTop.length > 0 && (
                <FormHelperText>
                  {t("{{suppliers}} share ranking {{rank}}. Choose which of them the call-off goes to.", {
                    suppliers: tiedAtTheTop.map((member) => member.name).join(", "),
                    rank: tiedAtTheTop[0].ranking,
                  })}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="call-off-title">{t("Title")}</FormLabel>
              <Input
                id="call-off-title"
                backgroundColor="smBackground"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired={remaining !== null} isInvalid={overCeiling}>
              <FormLabel htmlFor="call-off-value">{t("Estimated value")}</FormLabel>
              <InputGroup maxW="280px">
                <NumericInput
                  id="call-off-value"
                  backgroundColor="smBackground"
                  numericValue={estimatedValue}
                  placeholder="0"
                  onNumericChange={setEstimatedValue}
                />
                <InputRightAddon>{currency}</InputRightAddon>
              </InputGroup>
              {overCeiling ? (
                <FormErrorMessage>
                  {t("More than the {{remaining}} left under the framework agreement's ceiling value.", {
                    remaining: remainingText,
                  })}
                </FormErrorMessage>
              ) : (
                <FormHelperText>
                  {remaining === null
                    ? t("Excluding VAT")
                    : t("Excluding VAT. {{remaining}} left under the ceiling value.", { remaining: remainingText })}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            colorScheme="teal"
            isLoading={isLoading}
            isDisabled={title.trim() === "" || supplierOrganizationId === "" || overCeiling || valueMissing}
            onClick={submit}
          >
            {t("Create call-off")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
